/**
 * Integrations Settings Tab
 * Manage third-party API integrations (eFax, etc.) per agency
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Printer,
  Save,
  Loader2,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  ExternalLink,
  AlertTriangle,
  RefreshCw,
  TestTube2,
} from "lucide-react";
import { toast } from "sonner";
import { BrandingSettingsTab } from "./BrandingSettingsTab";
import FaxProviderSettings from "./FaxProviderSettings";
import SmsProviderSettings from "./SmsProviderSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const IntegrationsSettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  // eFax Settings
  const [efaxSettings, setEfaxSettings] = useState({
    enabled: false,
    api_key: "",
    api_secret: "",
    account_id: "",
    sender_fax_number: "",
    inbound_fax_number: "",
    inbound_email_notification: true,
    notification_emails: "",
  });
  
  // Show/hide sensitive fields
  const [showApiKey, setShowApiKey] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);
  
  // Connection status
  const [efaxStatus, setEfaxStatus] = useState(null); // null, 'connected', 'error'

  useEffect(() => {
    fetchIntegrationSettings();
  }, []);

  const fetchIntegrationSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings/integrations`);
      if (response.data.efax) {
        setEfaxSettings(prev => ({
          ...prev,
          ...response.data.efax,
          // Don't overwrite with masked values if we have real ones
          api_key: response.data.efax.api_key_set ? "••••••••••••••••" : "",
          api_secret: response.data.efax.api_secret_set ? "••••••••••••••••" : "",
          inbound_fax_number: response.data.efax.inbound_fax_number || "",
          inbound_email_notification: response.data.efax.inbound_email_notification ?? true,
          notification_emails: response.data.efax.notification_emails || "",
        }));
        setEfaxStatus(response.data.efax.status || null);
      }
    } catch (error) {
      console.error("Failed to fetch integration settings:", error);
      toast.error("Failed to load integration settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEfax = async () => {
    setSaving(true);
    try {
      // Only send fields that have changed (not masked values)
      const payload = {
        enabled: efaxSettings.enabled,
        account_id: efaxSettings.account_id,
        sender_fax_number: efaxSettings.sender_fax_number,
        inbound_fax_number: efaxSettings.inbound_fax_number,
        inbound_email_notification: efaxSettings.inbound_email_notification,
        notification_emails: efaxSettings.notification_emails,
      };
      
      // Only include API key/secret if they've been changed (not masked)
      if (efaxSettings.api_key && !efaxSettings.api_key.includes("•")) {
        payload.api_key = efaxSettings.api_key;
      }
      if (efaxSettings.api_secret && !efaxSettings.api_secret.includes("•")) {
        payload.api_secret = efaxSettings.api_secret;
      }
      
      await axios.put(`${API}/settings/integrations/efax`, payload);
      toast.success("eFax settings saved successfully");
      
      // Refresh to get updated status
      fetchIntegrationSettings();
    } catch (error) {
      console.error("Failed to save eFax settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save eFax settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const response = await axios.post(`${API}/settings/integrations/efax/test`);
      if (response.data.success) {
        setEfaxStatus("connected");
        toast.success("eFax connection successful!");
      } else {
        setEfaxStatus("error");
        toast.error(response.data.error || "Connection test failed");
      }
    } catch (error) {
      setEfaxStatus("error");
      toast.error(error.response?.data?.detail || "Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEfaxSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Email Branding section */}
      <BrandingSettingsTab />

      {/* Fax Provider (multi-provider: eFax + SRFax) */}
      <FaxProviderSettings />

      <SmsProviderSettings />

      {/* Legacy eFax Card (deprecated — use Fax Provider above) */}
      <Card style={{ display: "none" }}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Printer className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">eFax Corporate</CardTitle>
                <CardDescription>
                  Send and receive faxes for physician orders, referrals, and documents
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {efaxStatus === "connected" && (
                <Badge variant="default" className="bg-green-500">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Connected
                </Badge>
              )}
              {efaxStatus === "error" && (
                <Badge variant="destructive">
                  <XCircle className="w-3 h-3 mr-1" />
                  Error
                </Badge>
              )}
              <Switch
                checked={efaxSettings.enabled}
                onCheckedChange={(checked) => handleInputChange("enabled", checked)}
              />
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Info Alert */}
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              You need an eFax Corporate account to use this integration.{" "}
              <a 
                href="https://www.efax.com/corporate" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                Sign up here <ExternalLink className="w-3 h-3" />
              </a>
            </AlertDescription>
          </Alert>

          {/* Credentials Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="efax-account-id">Account ID</Label>
              <Input
                id="efax-account-id"
                value={efaxSettings.account_id}
                onChange={(e) => handleInputChange("account_id", e.target.value)}
                placeholder="Your eFax Account ID"
                disabled={!efaxSettings.enabled}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="efax-sender-number">Outbound Fax Number</Label>
              <Input
                id="efax-sender-number"
                value={efaxSettings.sender_fax_number}
                onChange={(e) => handleInputChange("sender_fax_number", e.target.value)}
                placeholder="+1XXXXXXXXXX"
                disabled={!efaxSettings.enabled}
              />
              <p className="text-xs text-slate-500">Your agency&apos;s outgoing fax number</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="efax-inbound-number">Inbound Fax Number</Label>
              <Input
                id="efax-inbound-number"
                value={efaxSettings.inbound_fax_number}
                onChange={(e) => handleInputChange("inbound_fax_number", e.target.value)}
                placeholder="+1XXXXXXXXXX"
                disabled={!efaxSettings.enabled}
              />
              <p className="text-xs text-slate-500">Number where you receive incoming faxes</p>
            </div>
            
            <div className="space-y-2 flex items-end">
              <div className="flex items-center gap-3 pb-2">
                <Switch
                  id="efax-email-notify"
                  checked={efaxSettings.inbound_email_notification}
                  onCheckedChange={(checked) => handleInputChange("inbound_email_notification", checked)}
                  disabled={!efaxSettings.enabled}
                />
                <Label htmlFor="efax-email-notify" className="text-sm cursor-pointer">
                  Email notification for incoming faxes
                </Label>
              </div>
            </div>
            
            {/* Notification Emails - Full width */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="efax-notification-emails">Notification Email Addresses</Label>
              <Input
                id="efax-notification-emails"
                value={efaxSettings.notification_emails}
                onChange={(e) => handleInputChange("notification_emails", e.target.value)}
                placeholder="admin@agency.com, billing@agency.com, intake@agency.com"
                disabled={!efaxSettings.enabled || !efaxSettings.inbound_email_notification}
              />
              <p className="text-xs text-slate-500">
                Comma-separated list of email addresses to receive incoming fax notifications
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="efax-api-key">API Key</Label>
              <div className="relative">
                <Input
                  id="efax-api-key"
                  type={showApiKey ? "text" : "password"}
                  value={efaxSettings.api_key}
                  onChange={(e) => handleInputChange("api_key", e.target.value)}
                  placeholder="Enter your eFax API Key"
                  disabled={!efaxSettings.enabled}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowApiKey(!showApiKey)}
                  disabled={!efaxSettings.enabled}
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="efax-api-secret">API Secret</Label>
              <div className="relative">
                <Input
                  id="efax-api-secret"
                  type={showApiSecret ? "text" : "password"}
                  value={efaxSettings.api_secret}
                  onChange={(e) => handleInputChange("api_secret", e.target.value)}
                  placeholder="Enter your eFax API Secret"
                  disabled={!efaxSettings.enabled}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowApiSecret(!showApiSecret)}
                  disabled={!efaxSettings.enabled}
                >
                  {showApiSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4 border-t">
            <Button
              onClick={handleSaveEfax}
              disabled={saving || !efaxSettings.enabled}
              className="gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Settings
                </>
              )}
            </Button>
            
            <Button
              variant="outline"
              onClick={handleTestConnection}
              disabled={testing || !efaxSettings.enabled || !efaxSettings.account_id}
              className="gap-2"
            >
              {testing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <TestTube2 className="w-4 h-4" />
                  Test Connection
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Future Integrations Placeholder */}
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="text-lg text-slate-500">More Integrations Coming Soon</CardTitle>
          <CardDescription>
            Twilio (SMS), QuickBooks, ADP, and more integrations will be available here. Email is handled by CarePilot's built-in managed sender — nothing to configure.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
};
